import React, { useState, useEffect } from 'react';

const EditSupplierModal = ({ supplier, onClose, onSave, groups = [] }) => {
  const [formData, setFormData] = useState({
    VendorID: '',
    BP_Name: '',
    Group_Code: '',
    Company_Reg: '',
    Contact_Person: '',
    E_Mail: '',
    Mobile_Phone: '',
    BBBEE_Type: '',
    BBBEE_Level: '',
    BBBEE_Expiration_Date: '',
    COIDA_Certificate_Number: '',
    COIDA_Certificate_Expiry_Date: '',
    Tax_Certification_Number: '',
    Tax_Certificate_Expiry_Date: '',
    Status: 1
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (supplier) {
      setFormData({
        VendorID: supplier.VendorID || '',
        BP_Name: supplier.BP_Name || '',
        Group_Code: supplier.Group_Code || '',
        Company_Reg: supplier.Company_Reg || '',
        Contact_Person: supplier.Contact_Person || '',
        E_Mail: supplier.E_Mail || '',
        Mobile_Phone: supplier.Mobile_Phone || '',
        BBBEE_Type: supplier.BBBEE_Type || '',
        BBBEE_Level: supplier.BBBEE_Level || '',
        BBBEE_Expiration_Date: supplier.BBBEE_Expiration_Date || '',
        COIDA_Certificate_Number: supplier.COIDA_Certificate_Number || '',
        COIDA_Certificate_Expiry_Date: supplier.COIDA_Certificate_Expiry_Date || '',
        Tax_Certification_Number: supplier.Tax_Certification_Number || '',
        Tax_Certificate_Expiry_Date: supplier.Tax_Certificate_Expiry_Date || '',
        Status: supplier.Status !== undefined ? supplier.Status : 1
      });
    }
  }, [supplier]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Helper function to safely get string value
    const getString = (value) => (value || '').toString();

    // Required fields - safely check with fallback
    if (!getString(formData.VendorID).trim()) {
      newErrors.VendorID = 'Vendor ID is required';
    }
    if (!getString(formData.BP_Name).trim()) {
      newErrors.BP_Name = 'Company Name is required';
    }
    if (!getString(formData.Group_Code).trim()) {
      newErrors.Group_Code = 'Group Code is required';
    }
    if (!getString(formData.Contact_Person).trim()) {
      newErrors.Contact_Person = 'Contact Person is required';
    }
    // if (!getString(formData.E_Mail).trim()) {
    //   newErrors.E_Mail = 'Email is required';
    // } else if (!/\S+@\S+\.\S+/.test(getString(formData.E_Mail))) {
    //   newErrors.E_Mail = 'Email format is invalid';
    // }

    // Date validation
    if (formData.BBBEE_Expiration_Date && !isValidDate(formData.BBBEE_Expiration_Date)) {
      newErrors.BBBEE_Expiration_Date = 'Invalid date format';
    }
    if (formData.COIDA_Certificate_Expiry_Date && !isValidDate(formData.COIDA_Certificate_Expiry_Date)) {
      newErrors.COIDA_Certificate_Expiry_Date = 'Invalid date format';
    }
    if (formData.Tax_Certificate_Expiry_Date && !isValidDate(formData.Tax_Certificate_Expiry_Date)) {
      newErrors.Tax_Certificate_Expiry_Date = 'Invalid date format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidDate = (dateString) => {
    return !isNaN(Date.parse(dateString));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await onSave(formData); // Let parent handle the API call
      setLoading(false);
      // The parent will close the modal on success
    } catch (error) {
      console.error('Error updating supplier:', error);
      setErrors({ submit: 'Failed to update supplier. Please try again.' });
      setLoading(false);
    }
  };

  const handleClose = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-y-auto">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Edit Supplier: {supplier?.BP_Name}
            </h3>
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Error message */}
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="text-sm text-red-700">{errors.submit}</div>
              </div>
            )}

            {/* BP Code (Read-only) */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-4">Supplier Code</h4>
              <div className="bg-gray-50 border border-gray-300 rounded-md p-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  BP Code
                </label>
                <span className="text-sm text-gray-600">{supplier?.BP_Code}</span>
              </div>
            </div>

            {/* Basic Information */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-4">Basic Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="VendorID" className="block text-sm font-medium text-gray-700 mb-1">
                    Vendor ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="VendorID"
                    name="VendorID"
                    value={formData.VendorID}
                    onChange={handleInputChange}
                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                      errors.VendorID ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter vendor ID"
                  />
                  {errors.VendorID && (
                    <p className="mt-1 text-sm text-red-600">{errors.VendorID}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="BP_Name" className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="BP_Name"
                    name="BP_Name"
                    value={formData.BP_Name}
                    onChange={handleInputChange}
                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                      errors.BP_Name ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter company name"
                  />
                  {errors.BP_Name && (
                    <p className="mt-1 text-sm text-red-600">{errors.BP_Name}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="Group_Code" className="block text-sm font-medium text-gray-700 mb-1">
                    Group Code <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="Group_Code"
                    name="Group_Code"
                    value={formData.Group_Code}
                    onChange={handleInputChange}
                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                      errors.Group_Code ? 'border-red-300' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select group</option>
                    {groups.map((group) => (
                      <option key={group.Supplier_Group_Code} value={group.Supplier_Group_Code}>
                        {group.Supplier_Group_Code} - {group.Supplier_Group_Name}
                      </option>
                    ))}
                  </select>
                  {errors.Group_Code && (
                    <p className="mt-1 text-sm text-red-600">{errors.Group_Code}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="Company_Reg" className="block text-sm font-medium text-gray-700 mb-1">
                    Company Registration
                  </label>
                  <input
                    type="text"
                    id="Company_Reg"
                    name="Company_Reg"
                    value={formData.Company_Reg}
                    onChange={handleInputChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Enter company registration number"
                  />
                </div>

                <div>
                  <label htmlFor="Contact_Person" className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Person <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="Contact_Person"
                    name="Contact_Person"
                    value={formData.Contact_Person}
                    onChange={handleInputChange}
                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                      errors.Contact_Person ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter contact person name"
                  />
                  {errors.Contact_Person && (
                    <p className="mt-1 text-sm text-red-600">{errors.Contact_Person}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="E_Mail" className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="E_Mail"
                    name="E_Mail"
                    value={formData.E_Mail}
                    onChange={handleInputChange}
                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                      errors.E_Mail ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter email address"
                  />
                  {errors.E_Mail && (
                    <p className="mt-1 text-sm text-red-600">{errors.E_Mail}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="Mobile_Phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Mobile Phone
                  </label>
                  <input
                    type="tel"
                    id="Mobile_Phone"
                    name="Mobile_Phone"
                    value={formData.Mobile_Phone}
                    onChange={handleInputChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Enter mobile phone number"
                  />
                </div>

                <div>
                  <label htmlFor="Status" className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    id="Status"
                    name="Status"
                    value={formData.Status}
                    onChange={handleInputChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            {/* BBBEE Information */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-4">BBBEE Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="BBBEE_Type" className="block text-sm font-medium text-gray-700 mb-1">
                    BBBEE Type
                  </label>
                  <input
                    type="text"
                    id="BBBEE_Type"
                    name="BBBEE_Type"
                    value={formData.BBBEE_Type}
                    onChange={handleInputChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Enter BBBEE type"
                  />
                </div>

                <div>
                  <label htmlFor="BBBEE_Level" className="block text-sm font-medium text-gray-700 mb-1">
                    BBBEE Level
                  </label>
                  <input
                    type="text"
                    id="BBBEE_Level"
                    name="BBBEE_Level"
                    value={formData.BBBEE_Level}
                    onChange={handleInputChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Enter BBBEE level"
                  />
                </div>

                <div>
                  <label htmlFor="BBBEE_Expiration_Date" className="block text-sm font-medium text-gray-700 mb-1">
                    BBBEE Expiry Date
                  </label>
                  <input
                    type="date"
                    id="BBBEE_Expiration_Date"
                    name="BBBEE_Expiration_Date"
                    value={formData.BBBEE_Expiration_Date}
                    onChange={handleInputChange}
                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                      errors.BBBEE_Expiration_Date ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.BBBEE_Expiration_Date && (
                    <p className="mt-1 text-sm text-red-600">{errors.BBBEE_Expiration_Date}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Compliance Information */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-4">Compliance Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="COIDA_Certificate_Number" className="block text-sm font-medium text-gray-700 mb-1">
                    COIDA Certificate Number
                  </label>
                  <input
                    type="text"
                    id="COIDA_Certificate_Number"
                    name="COIDA_Certificate_Number"
                    value={formData.COIDA_Certificate_Number}
                    onChange={handleInputChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Enter COIDA certificate number"
                  />
                </div>

                <div>
                  <label htmlFor="COIDA_Certificate_Expiry_Date" className="block text-sm font-medium text-gray-700 mb-1">
                    COIDA Expiry Date
                  </label>
                  <input
                    type="date"
                    id="COIDA_Certificate_Expiry_Date"
                    name="COIDA_Certificate_Expiry_Date"
                    value={formData.COIDA_Certificate_Expiry_Date}
                    onChange={handleInputChange}
                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                      errors.COIDA_Certificate_Expiry_Date ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.COIDA_Certificate_Expiry_Date && (
                    <p className="mt-1 text-sm text-red-600">{errors.COIDA_Certificate_Expiry_Date}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="Tax_Certification_Number" className="block text-sm font-medium text-gray-700 mb-1">
                    Tax Certificate Number
                  </label>
                  <input
                    type="text"
                    id="Tax_Certification_Number"
                    name="Tax_Certification_Number"
                    value={formData.Tax_Certification_Number}
                    onChange={handleInputChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Enter tax certificate number"
                  />
                </div>

                <div>
                  <label htmlFor="Tax_Certificate_Expiry_Date" className="block text-sm font-medium text-gray-700 mb-1">
                    Tax Certificate Expiry Date
                  </label>
                  <input
                    type="date"
                    id="Tax_Certificate_Expiry_Date"
                    name="Tax_Certificate_Expiry_Date"
                    value={formData.Tax_Certificate_Expiry_Date}
                    onChange={handleInputChange}
                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                      errors.Tax_Certificate_Expiry_Date ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.Tax_Certificate_Expiry_Date && (
                    <p className="mt-1 text-sm text-red-600">{errors.Tax_Certificate_Expiry_Date}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Updating...' : 'Update Supplier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSupplierModal;
